import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import logger from "../../../art-logger-config";
import { AppThunk } from "../../app/store";
import { searchAggregations } from "../../app/api";
import {
  AggregationFacet,
  AggregationResult,
  ScalarAggregationResults,
  SearchAggregationResult,
} from "../../../from-server/asin-central/search";
import { STATUS_ATTRIBUTE_SPECS } from "./chartData";

export type StatusOverviewAggregationStatus =
  | "idle"
  | "loading"
  | "success"
  | "error";

export interface StatusOverviewAggregationState {
  status: StatusOverviewAggregationStatus;
  totalCount: number;
  aggregationResults: AggregationResult;
  earliestValidationRunAtMs: number | undefined;
  error: string | null;
  lastFetchedQueryString: string | null;
}

const initialState: StatusOverviewAggregationState = {
  status: "idle",
  totalCount: 0,
  aggregationResults: {},
  earliestValidationRunAtMs: undefined,
  error: null,
  lastFetchedQueryString: null,
};

// Each status attribute has ~3–4 distinct values; 10 leaves headroom
// while keeping response payloads small.
const MAX_BUCKETS_PER_ATTRIBUTE = 10;

// Validation-audit attributes whose `.lastUpdatedAt` timestamps the
// backend surfaces as scalar MIN aggregations. The client selects the
// earliest across all four so the header info line reflects the oldest
// piece of validation state in the current filtered set. The names
// here MUST match registered DACS attributes — DACS resolves
// `<attributeName>.lastUpdatedAt` server-side for MIN facets, so we
// send the raw attribute (e.g. `validationRequestLVS`), not the
// client-side "Audits" wrapper name (`validationRequestLVSAudits`).
const VALIDATION_AUDIT_ATTRIBUTES = [
  "validationRequestLVS",
  "validationRequestHattrick",
  "validationDetailResultLVS",
  "validationDetailResultHattrick",
] as const;

function coerceScalarToTimestampMs(value: unknown): number | undefined {
  if (typeof value !== "number") return undefined;
  if (!Number.isFinite(value)) return undefined;
  if (value <= 0) return undefined;
  return value;
}

function computeEarliestValidationRunAtMs(
  scalars: ScalarAggregationResults | undefined
): number | undefined {
  if (!scalars) return undefined;
  let earliest: number | undefined = undefined;
  for (const attr of VALIDATION_AUDIT_ATTRIBUTES) {
    const candidate = coerceScalarToTimestampMs(scalars[attr]);
    if (candidate === undefined) continue;
    if (earliest === undefined || candidate < earliest) {
      earliest = candidate;
    }
  }
  return earliest;
}

const statusOverviewAggregation = createSlice({
  name: "statusOverviewAggregation",
  initialState,
  reducers: {
    statusOverviewAggregationStart(
      state: StatusOverviewAggregationState,
      { payload }: PayloadAction<{ queryString: string }>
    ) {
      // Fresh filter: wipe the prior bars so the container shows its
      // spinner ("Loading status overview…") instead of leaving stale
      // bars visible while the new aggregation runs.
      if (payload.queryString !== state.lastFetchedQueryString) {
        state.aggregationResults = {};
        state.totalCount = 0;
      }
      state.status = "loading";
      state.error = null;
      state.lastFetchedQueryString = payload.queryString;
      state.earliestValidationRunAtMs = undefined;
    },
    statusOverviewAggregationSuccess(
      state: StatusOverviewAggregationState,
      { payload }: PayloadAction<SearchAggregationResult>
    ) {
      state.status = "success";
      state.totalCount = payload.totalResultCount ?? 0;
      state.aggregationResults = payload.aggregationResults ?? {};
      state.earliestValidationRunAtMs = computeEarliestValidationRunAtMs(
        payload.scalarAggregationResults
      );
      state.error = null;
    },
    statusOverviewAggregationFailure(
      state: StatusOverviewAggregationState,
      { payload }: PayloadAction<string>
    ) {
      state.status = "error";
      state.error = payload;
      state.earliestValidationRunAtMs = undefined;
    },
    resetStatusOverviewAggregation() {
      return initialState;
    },
  },
});

export const {
  statusOverviewAggregationStart,
  statusOverviewAggregationSuccess,
  statusOverviewAggregationFailure,
  resetStatusOverviewAggregation,
} = statusOverviewAggregation.actions;

export default statusOverviewAggregation.reducer;

function buildStatusAggregationFacets(): AggregationFacet[] {
  return [
    ...STATUS_ATTRIBUTE_SPECS.map((spec) => ({
      attributeName: spec.attributeKey,
      aggregationType: "terms",
      totalAggregationCount: MAX_BUCKETS_PER_ATTRIBUTE,
    })),
    ...VALIDATION_AUDIT_ATTRIBUTES.map((attr) => ({
      attributeName: attr,
      aggregationType: "min",
      totalAggregationCount: 0,
    })),
  ];
}

// Guards against out-of-order responses: only commit `success` /
// `failure` when this thunk owns the most recent
// `lastFetchedQueryString`. A slower response to an older query is
// dropped so it can't overwrite fresher data.
export const fetchStatusOverviewAggregation =
  (queryString: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(statusOverviewAggregationStart({ queryString }));
    try {
      const result = await searchAggregations(
        queryString,
        buildStatusAggregationFacets()
      );
      if (
        getState().statusOverviewAggregation.lastFetchedQueryString ===
        queryString
      ) {
        dispatch(statusOverviewAggregationSuccess(result));
      }
    } catch (err: unknown) {
      if (
        getState().statusOverviewAggregation.lastFetchedQueryString ===
        queryString
      ) {
        const message = err instanceof Error ? err.message : String(err);
        logger.error(`Status overview aggregation request failed: ${message}`);
        dispatch(statusOverviewAggregationFailure(message));
      }
    }
  };
